import type { PlainDate, PlainDateString } from "@sa-nu/shared-utils/date";
import { toPlainDateString } from "@sa-nu/shared-utils/date";
import { formatCurrency } from "@sa-nu/shared-utils/price";

import type { DatePickerDateInfo } from "../../dx/ui/date-picker";
import { UNAVAILABLE_PRICE_LABEL } from "../../reservation-form/_shared/price-labels";

// テストで GraphQL 生成型に依存させないための、この関数専用の最小シェイプ。
// hook 側 (use-search-calendar-availability.ts) にクエリ由来の同名型が存在するため、
// 衝突を避けて `Nights` を付けている。
export type SearchCalendarNight = {
  night: PlainDateString;
  isAvailable: boolean;
  price: number;
  maxConsecutiveNights: number;
};

export type SearchCalendarNightsRoomType = {
  availabilities: {
    nights: readonly SearchCalendarNight[];
  };
};

export type SearchCalendarNightsSite = {
  roomTypesForReservation: readonly SearchCalendarNightsRoomType[];
};

export type SearchCalendarNightsArea = {
  sitesForReservation: readonly SearchCalendarNightsSite[];
};

/**
 * DatePicker が描画に使う DatePickerDateInfo に、CO 候補判定専用の maxConsecutiveNights を
 * 加えたもの。DatePicker 自体には渡さず、applySearchCalendarCheckoutPhase の入力としてのみ使う。
 */
export type SearchCalendarDateInfo = DatePickerDateInfo & {
  maxConsecutiveNights: number;
};

type NightAggregate = {
  isAvailable: boolean;
  minPrice: number;
  maxConsecutiveNights: number;
};

const INITIAL_AGGREGATE: NightAggregate = {
  isAvailable: false,
  minPrice: Infinity,
  maxConsecutiveNights: 0,
};

// 満室 roomType の price は「通せない日程の価格」であり最安値の候補にならないため、
// isAvailable な night の price だけを MIN 集計対象にする。
function mergeNightAggregate(
  acc: NightAggregate,
  night: SearchCalendarNight,
): NightAggregate {
  return {
    isAvailable: acc.isAvailable || night.isAvailable,
    minPrice: night.isAvailable
      ? Math.min(acc.minPrice, night.price)
      : acc.minPrice,
    maxConsecutiveNights: Math.max(
      acc.maxConsecutiveNights,
      night.maxConsecutiveNights,
    ),
  };
}

function aggregateNightsByDate(
  areas: readonly SearchCalendarNightsArea[],
): Map<PlainDateString, NightAggregate> {
  const nights = areas.flatMap((area) =>
    area.sitesForReservation.flatMap((site) =>
      site.roomTypesForReservation.flatMap(
        (roomType) => roomType.availabilities.nights,
      ),
    ),
  );

  return nights.reduce((acc, night) => {
    acc.set(
      night.night,
      mergeNightAggregate(acc.get(night.night) ?? INITIAL_AGGREGATE, night),
    );
    return acc;
  }, new Map<PlainDateString, NightAggregate>());
}

function formatPrice(price: number): string {
  return `${formatCurrency(price)}~`;
}

/**
 * 全 area/site/roomType 横断で夜ごとに isAvailable（OR）/ minPrice（available な中での MIN）/
 * maxConsecutiveNights（MAX、いずれかの roomType で実際に通しカバーできる連泊数）を算出する。
 * `dates` は描画対象の全日付。nights に現れない日付（対象 roomType が 0 件、または単純なギャップ）
 * も disabled として出力するため、集計結果とは別に描画対象の日付一覧を受け取る。
 */
export function buildSearchCalendarDateInfoMap(
  areas: readonly SearchCalendarNightsArea[],
  dates: readonly PlainDateString[],
): Record<PlainDateString, SearchCalendarDateInfo> {
  const aggregateByDate = aggregateNightsByDate(areas);

  return dates.reduce<Record<PlainDateString, SearchCalendarDateInfo>>(
    (result, date) => {
      const aggregate = aggregateByDate.get(date);
      const maxConsecutiveNights = aggregate?.maxConsecutiveNights ?? 0;
      result[date] =
        aggregate !== undefined && aggregate.isAvailable
          ? {
              disabled: false,
              price: formatPrice(aggregate.minPrice),
              maxConsecutiveNights,
            }
          : { disabled: true, price: undefined, maxConsecutiveNights };
      return result;
    },
    {},
  );
}

const EMPTY_DATE_INFO: SearchCalendarDateInfo = {
  disabled: true,
  maxConsecutiveNights: 0,
};

/**
 * CO 選択フェーズ用に baseMap（CI フェーズの静的マップ）の disabled を上書きする。
 * CI の夜の maxConsecutiveNights（いずれかの roomType で実際に通しカバーできる連泊数）と
 * maxStayNights（会員プランの契約上限、日付非依存）の両方を満たす必要があるため min を実効上限とし、
 * CI+1 日 〜 CI+実効上限日を CO 候補にする。
 */
export function applySearchCalendarCheckoutPhase(
  baseMap: Record<PlainDateString, SearchCalendarDateInfo>,
  dates: readonly PlainDateString[],
  startDate: PlainDate,
  maxStayNights: number | null,
): Record<PlainDateString, DatePickerDateInfo> {
  const startDateKey = toPlainDateString(startDate);
  const ciMaxConsecutiveNights =
    baseMap[startDateKey]?.maxConsecutiveNights ?? 0;
  const effectiveMaxNights =
    maxStayNights !== null
      ? Math.min(ciMaxConsecutiveNights, maxStayNights)
      : ciMaxConsecutiveNights;

  const checkoutCandidates = new Set<PlainDateString>(
    Array.from({ length: effectiveMaxNights }, (_, offset) =>
      toPlainDateString(startDate.add({ days: offset + 1 })),
    ),
  );

  return dates.reduce<Record<PlainDateString, DatePickerDateInfo>>(
    (result, date) => {
      // maxConsecutiveNights は DatePicker 自体に日付依存の在庫上限ロジックを持ち込まない設計のため、
      // DatePicker へ渡す結果には含めない。
      const { maxConsecutiveNights: _maxConsecutiveNights, ...base } =
        baseMap[date] ?? EMPTY_DATE_INFO;
      result[date] = {
        ...base,
        disabled: date === startDateKey ? false : !checkoutCandidates.has(date),
      };
      return result;
    },
    {},
  );
}

/**
 * CI/CO 確定フェーズ用に baseMap（選択非依存の静的マップ）を上書きする。
 * CO 日（endDate）は宿泊しない日のため、price だけ UNAVAILABLE_PRICE_LABEL に上書きし
 * disabled は baseMap の値を維持する。
 */
export function applySearchCalendarConfirmedPhase(
  baseMap: Record<PlainDateString, DatePickerDateInfo>,
  endDate: PlainDate,
): Record<PlainDateString, DatePickerDateInfo> {
  const endDateKey = toPlainDateString(endDate);
  const base = baseMap[endDateKey];
  if (base === undefined) return baseMap;

  return {
    ...baseMap,
    [endDateKey]: { ...base, price: UNAVAILABLE_PRICE_LABEL },
  };
}
